// =================================================================================================
// © 2021 Shrewd Apps
// ALL RIGHTS RESERVED.

import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "reactstrap";
import { Table } from "reactstrap";
import { Badge } from "react-bootstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Helmet } from "react-helmet";
import { Nav, NavItem, NavLink } from "reactstrap";

import { constructGetTopNewCardsUrl } from "api";
import { metagameMultiFormatCardStats } from "apiSchemas";

import PageErrorBoundary from "global/errors/PageErrorBoundary";
import FetchSection from "global/loading/FetchSection";

//import LinkToMetagameFormats from "common/links/LinkToMetagameFormats";
//import LinkToMetagameFormatPeriod from "common/links/LinkToMetagameFormatPeriod";
import LinkToHomePage from "common/links/LinkToHomePage";
import { getFormatDisplayName } from "common/resources/formatNames";
import { formatInteger, formatPrice, formatPriceChange } from "global/utilities/format";
import LinkToCard from "common/links/LinkToCard";
//import TableCellItem from "common/TableCellItem";
//import ColorIdentities from "common/ColorIdentities";
//import ColorIdentity from "common/ColorIdentity";
import CardRarityImagePopover from "common/CardRarityImagePopover";
import ManaCost from "common/ManaCost";
import { getCurrentDateIso8601 } from "global/utilities/Date";
import { parseUrlSearchParameters } from "global/utilities/parseUrlSearchParameters";

// -----------------------------------------------------------------------------
export default function MetagameTopCardsPage() {
	const location = useLocation();
	const urlFilterParameters = parseUrlSearchParameters(location.search);
	const filterParameters = getFilterParameters(urlFilterParameters);
	const uiFilterParameters = getUIFilterParameters(urlFilterParameters);

	return (
		<PageErrorBoundary>
			<Container>
				<>
					<Row>
						<Col>
							<Breadcrumbs />
						</Col>
					</Row>

					<Row>
						<Col>
							<h2 className="mt-2">
								Top New Cards
							</h2>
						</Col>
					</Row>

					<Row>
						<Col>
							<FilterBubbles uiFilterParameters={uiFilterParameters} />
						</Col>
					</Row>

					<FetchSection requestUrl={constructGetTopNewCardsUrl(150)}>
						{data =>
							<Row>
								<Col>
									{data && <MetagameTopCardsTable cards={data} filterParameters={filterParameters} />}
								</Col>
							</Row>
						}
					</FetchSection>
				</>
			</Container>
		</PageErrorBoundary>
	);
}

// -----------------------------------------------------------------------------
export function MetagameTopCardsTable({ cards, filterParameters }) {
	let filteredCards = cards;
	if (filterParameters && filterParameters.formatId) {
		const formatId = filterParameters.formatId;
		const topCardFilter = (i_card1, i_card2) => {
			return i_card2.numberOfCopies[formatId] - i_card1.numberOfCopies[formatId];
		};

		filteredCards = filteredCards.
			filter(i_card => i_card.numberOfCopies[formatId]).
			sort(topCardFilter);
	}

	return (
		<Table striped size="sm">
			<thead>
				<tr>
					<th>Card</th>
					<th className="text-center">Number of Copies</th>
					<th className="text-end d-none d-md-table-cell">Cost</th>
				</tr>
			</thead>

			<tbody>
				{(filteredCards && filteredCards.length > 0) ?
					filteredCards.map(card =>
						<MetagameTopCardsRow
							key={card.cardId}
							card={card}
							filterParameters={filterParameters} />) :

					// No cards found
					<tr className="text-muted text-center">
						<td colSpan={3}>
							No cards
						</td>
					</tr>
				}
			</tbody>
		</Table>
	);
}

MetagameTopCardsTable.propTypes = {
	cards: PropTypes.arrayOf(PropTypes.shape(metagameMultiFormatCardStats)),
	filterParameters: PropTypes.object
};

// -----------------------------------------------------------------------------
function MetagameTopCardsRow({ card, filterParameters }) {
	const formatId = filterParameters && filterParameters.formatId;
	let formatIds = Object.keys(card.numberOfCopies);

	if (formatId) {
		formatIds = [formatId];
	}

	return (
		<tr>
			<td>
				<CardRarityImagePopover cardId={card.cardId} multiface={card.multiface} cardVariation={card.cardVariation} rarity={card.cardRarity} size="Small" style={{ maxHeight: 16, maxWidth: 24, margin: 0 }} deckLocation="cards" />
				{" "}
				<LinkToCard cardId={card.cardId} multiface={card.multiface}>
					{card.cardName}
				</LinkToCard>
				{" "}
				<ManaCost cost={card.cardManaCost} />
			</td>

			<td className="text-center">
				{card.numberOfDecks && formatIds.map(key =>
					<Link to={`/top/cards?formats=${key}`}>	
						<Badge className="ms-1" bg="info" text="light" key={key}>{getFormatDisplayName(key)}{": "}{formatInteger(card.numberOfCopies[key])}</Badge>
					</Link>
				)}
			</td>

			<td className="text-end d-none d-md-table-cell" style={{ maxWidth: "150px" }}>
				{formatPriceChange(card.recentPriceDirection) }
				{formatPrice(card.price)}
			</td>
		</tr>
	);
}

MetagameTopCardsRow.propTypes = {
	card: PropTypes.shape(metagameMultiFormatCardStats),
	filterParameters: PropTypes.object
};

// -------------------------------------------------------------------------------------------------
function Breadcrumbs() {
	const rootDomain = `${window.location.protocol}//${window.location.hostname}`;
	const title = "Top New Cards | Magic the Gathering | MTG Top Decks";
	const shortTitle = "Top New Cards";
	const description = "List of most played cards in top decks from current Magic the Gathering set";
	const keywords = `Magic the Gathering, cards, metagame, decks, tournaments`;
	const date = getCurrentDateIso8601();
	const breadcrumbsLdJson = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement:": [{
			"@type": "ListItem",
			"position": 1,
			"name": "Home",
			"item": rootDomain
		},
		{
			"@type": "ListItem",
			"position": 2,
			"name": "Top New Cards"
		}]
	};

	const pageLdJson = {
		"@context": "https://schema.org",
		"@type": "Article",
		"description": description,
		"keywords": keywords,
		"dateCreated": date,
		"dateModified": date,
		"headline": title,
		"image": [
			rootDomain + "/assets/logo.png"
		],
		"author": [{
			"@type": "Organization",
			"name": "MTG Top Decks",
			"url": rootDomain
		}]
	};

	return (
		<>
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:title" content={shortTitle} />
				<meta property="og:description" content={description} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={window.location.href} />
				<meta property="og:modified_time" content={date} />
				<meta property="og:site_name" content="MTG Top Decks" />
				<script type="application/ld+json">{JSON.stringify(breadcrumbsLdJson)}</script>
				<script type="application/ld+json">{JSON.stringify(pageLdJson)}</script>
			</Helmet>
			<Breadcrumb>
				<BreadcrumbItem><LinkToHomePage>Home</LinkToHomePage></BreadcrumbItem>
				<BreadcrumbItem active>Top New Cards</BreadcrumbItem>
			</Breadcrumb>
		</>
	);
}

Breadcrumbs.propTypes = {
	formatId: PropTypes.string
};

// -------------------------------------------------------------------------------------------------
function getFilterParameters(urlFilterParameters) {
	let filterParameters = {};

	if (urlFilterParameters && urlFilterParameters.formats) {
		filterParameters.formatId = urlFilterParameters.formats;
	}

	return filterParameters;
}

// -------------------------------------------------------------------------------------------------
function getUIFilterParameters(urlFilterParameters) {
	let filterParameters = [];

	if (urlFilterParameters && urlFilterParameters.formats) {
		// FUTURE: Handle collection of formats
		filterParameters.push({
			displayName: "Format",
			displayValue: getFormatDisplayName(urlFilterParameters.formats),
			internalValue: urlFilterParameters.formats
		});
	}

	return filterParameters;
}

// -----------------------------------------------------------------------------
// FUTURE: Calculate link so it can keep other filters rather than clearing all
function FilterBubbles({ uiFilterParameters }) {
	return (uiFilterParameters && uiFilterParameters.length > 0 &&
		<Nav pills className="m-2">
			{uiFilterParameters.map((uiFilterParameter, index) =>
				<NavItem key={index}>
					<NavLink to="/top/cards" tag={Link} active>
						<FilterBubble uiFilterParameter={uiFilterParameter} />
					</NavLink>
				</NavItem>)
			}
		</Nav>
	);
}

const uiFilterParameterSchema = {
	displayName: PropTypes.string.isRequired,
	displayValue: PropTypes.string,
	internalValue: PropTypes.string
};

FilterBubbles.propTypes = {
	uiFilterParameters: PropTypes.arrayOf(PropTypes.shape(uiFilterParameterSchema))
};

// -----------------------------------------------------------------------------
function FilterBubble({ uiFilterParameter }) {
	return (
		<>
			{uiFilterParameter.displayName}: {uiFilterParameter.displayValue ? uiFilterParameter.displayValue : "<unknown>"}
			{" "}
			<span className="fas fa-times-circle" />
		</>
	);
}

FilterBubble.propTypes = {
	uiFilterParameter: PropTypes.shape(uiFilterParameterSchema)
};
